/**
 * useBusinessInfo — single source of truth for business contact details.
 *
 * Reads values that were saved in BusinessInfoPage (biz:contact:* keys)
 * through the ContentEditing context, falling back to static siteConfig.
 *
 * Header, Footer, and any other component that needs phone / email / address
 * should consume this hook so they stay in sync with BusinessInfoPage.
 */

import { useContentEditingSafe } from "./ContentEditingContext";
import { contact as brandingContact, hours as brandingHours } from "../../siteConfig";
import { contact as storeContact, hours as storeHours } from "../../storeSiteConfig";
import { useTemplateType } from "./TemplateContext";

function stripNonDigits(s: string): string {
  return s.replace(/[^0-9+]/g, "");
}

export function useBusinessInfo() {
  const ctx = useContentEditingSafe();
  const templateType = useTemplateType();
  const isStore = templateType === "store";
  const staticContact = isStore ? storeContact : brandingContact;
  const staticHours = isStore ? storeHours : brandingHours;

  /** Get a biz:contact value; returns empty string if not saved yet. */
  const raw = (key: string): string => {
    if (!ctx) return "";
    return ctx.getContent(key, "");
  };

  /* ── Derived values (KV override → siteConfig fallback) ── */

  const phone = raw("biz:contact:seo-phone") || staticContact.phone;
  const directPhone = raw("biz:contact:direct-phone") || "";
  const whatsapp = raw("biz:contact:whatsapp") || staticContact.mobile || "";
  const mobile = whatsapp; // alias used by Footer
  const email = raw("biz:contact:email") || staticContact.email;
  const address = raw("biz:contact:address") || staticContact.address;
  const hoursStr = raw("biz:contact:hours") || "";

  /* ── Hours — return as array for Footer map() ── */
  const hours: { days: string; time: string }[] = hoursStr
    ? hoursStr.split(",").map((chunk) => {
        const trimmed = chunk.trim();
        // Try to split "א-ה 09:00-18:00" into days + time
        const parts = trimmed.split(/\s+/);
        if (parts.length >= 2) {
          return { days: parts.slice(0, -1).join(" "), time: parts[parts.length - 1] };
        }
        return { days: trimmed, time: "" };
      })
    : staticHours;

  /* ── Computed hrefs ── */

  const phoneHref = `tel:${stripNonDigits(phone)}`;
  const emailHref = `mailto:${email}`;

  const whatsappDigits = whatsapp ? stripNonDigits(whatsapp).replace(/^0/, "") : "";
  const whatsappUrl = whatsappDigits
    ? `https://wa.me/972${whatsappDigits}`
    : staticContact.whatsappUrl || "";

  /* ── Social links ── */

  const facebook = raw("biz:contact:facebook") || "";
  const instagram = raw("biz:contact:instagram") || "";
  const youtube = raw("biz:contact:youtube") || "";
  const linkedin = raw("biz:contact:linkedin") || "";
  const gmb = raw("biz:contact:gmb") || "";
  const otherSocial = raw("biz:contact:other-social") || "";

  return {
    phone,
    directPhone,
    whatsapp,
    mobile,
    email,
    address,
    hoursStr,
    hours,
    phoneHref,
    emailHref,
    whatsappUrl,
    facebook,
    instagram,
    youtube,
    linkedin,
    gmb,
    otherSocial,
  };
}
